import { Link, useParams, createFileRoute } from '@tanstack/react-router';
import PageTransition from '@/components/ui/PageTransition';
import { DetailPageHeader } from '@/components/ui/DetailPageHeader';
import { ErrorAlert } from '@/components/shared/ErrorAlert';
import { SummaryStatCard } from '@/components/shared/summary/SummaryStatCard';
import { SummarySectionCard } from '@/components/shared/summary/SummarySectionCard';
import { InfoRow } from '@/components/shared/summary/InfoRow';
import { formatScore, getScoreColor, getRecommendationColor, getConfidenceColor } from '@/utils/score';
import { formatDateTime } from '@/utils/dateFormat';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Space,
  Button,
  Progress,
  theme,
  Statistic,
  Badge,
  Skeleton
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  TrophyOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  StarOutlined,
  FileTextOutlined,
  BulbOutlined,
  LikeOutlined,
  DislikeOutlined,
  ThunderboltOutlined,
  SafetyOutlined,
  FireOutlined,
  RocketOutlined,
  IdcardOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  MailOutlined,
  SolutionOutlined
} from '@ant-design/icons';
import {
  useJobModuleApplicationQuery,
  useJobModuleApplicationSummaryQuery
} from '../../../api';
import { useTranslation } from 'react-i18next';
import { createModuleRouteGuard } from '@/modules/routeGuards';
import { MODULE_BASE_ROUTE } from '../../../index';

const { Title, Text, Paragraph } = Typography;
const { useToken } = theme;

/** Long date-time format used for the summary metadata rows. */
const SUMMARY_DATE_TIME_FORMAT = 'MMMM D, YYYY [at] h:mm A';

/**
 * Decodes HTML entities in AI evidence text (e.g. `&apos;`) into plain characters.
 */
function decodeHtmlEntities(text: string): string {
  if (typeof window !== 'undefined') {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }
  // Fallback for SSR: decode common entities manually
  return text
    .replace(/&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
}

// File-based route definition using virtual file routes
// Note: Must use string literal for createFileRoute (build-time requirement)
// MODULE_BASE_ROUTE is still used for Links and other runtime uses
export const Route = createFileRoute('/manage/modules/jobModule/applications/$applicationId/summary')({
  component: ApplicationSummaryPage,
  beforeLoad: createModuleRouteGuard('job-module')
});

export function ApplicationSummaryPage() {
  const { token } = useToken();
  const { t } = useTranslation('job-module');
  // Use string literal for useParams to match the route definition
  const { applicationId } = useParams({ from: '/manage/modules/jobModule/applications/$applicationId/summary' });

  const { data: application, isLoading: applicationLoading } = useJobModuleApplicationQuery(applicationId);
  const { data: summary, isLoading: summaryLoading, error: summaryError } = useJobModuleApplicationSummaryQuery(applicationId);

  const isLoading = applicationLoading || summaryLoading;

  if (isLoading) {
    return (
      <PageTransition animation="fadeInUp">
        <div style={{ marginBottom: token.marginLG }}>
          <Space style={{ marginBottom: token.marginMD }}>
            <Skeleton.Button active size="medium" style={{ width: 'min(180px, 100%)' }} />
          </Space>
          <div>
            <Skeleton.Input active size="large" style={{ width: 'min(300px, 100%)', marginBottom: token.marginXS }} />
            <div style={{ marginTop: token.marginXS }}>
              <Skeleton.Input active size="small" style={{ width: 'min(400px, 100%)' }} />
            </div>
          </div>
        </div>

        {/* Final Score & Recommendation Cards Skeleton */}
        <Row gutter={[16, 16]} style={{ marginBottom: token.marginLG }}>
          <Col xs={24} md={8}>
            <Card>
              <Skeleton active paragraph={{ rows: 2 }} />
              <Skeleton.Input active size="small" style={{ width: '100%', marginTop: token.marginMD }} />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <Skeleton active paragraph={{ rows: 3 }} />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <Skeleton active paragraph={{ rows: 3 }} />
            </Card>
          </Col>
        </Row>

        {/* Overall Fit Summary Skeleton */}
        <Card
          style={{ marginBottom: token.marginLG }}
          styles={{
            body: { padding: token.paddingLG }
          }}
        >
          <Skeleton active title={{ width: 200 }} paragraph={{ rows: 4 }} />
        </Card>

        {/* Pros and Cons Skeleton */}
        <Row gutter={[16, 16]} style={{ marginBottom: token.marginLG }}>
          <Col xs={24} lg={12}>
            <Card>
              <Skeleton active title={{ width: 150 }} paragraph={{ rows: 5 }} />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card>
              <Skeleton active title={{ width: 200 }} paragraph={{ rows: 5 }} />
            </Card>
          </Col>
        </Row>

        {/* Detailed Score Breakdown Skeleton */}
        <Card
          style={{ marginBottom: token.marginLG }}
          styles={{
            body: { padding: token.paddingLG }
          }}
        >
          <Skeleton active title={{ width: 250 }} />
          <Row gutter={[16, 16]} style={{ marginTop: token.marginMD }}>
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <Col xs={24} sm={12} md={8} lg={6} key={item}>
                <Card size="small">
                  <Skeleton active paragraph={{ rows: 2 }} />
                </Card>
              </Col>
            ))}
          </Row>
        </Card>

        {/* Metadata Skeleton */}
        <Card>
          <Skeleton active paragraph={{ rows: 3 }} />
        </Card>
      </PageTransition>
    );
  }

  if (summaryError || !summary) {
    return (
      <PageTransition animation="fadeInUp">
        <Card>
          <ErrorAlert
            title={t('summary.errorLoading')}
            error={summaryError}
            fallbackDescription={t('summary.errorDescription')}
            action={
              <Link to={MODULE_BASE_ROUTE}>
                <Button>{t('summary.backToApplications')}</Button>
              </Link>
            }
          />
        </Card>
      </PageTransition>
    );
  }

  const { summary: summaryData } = summary;
  const { scores, pros, cons, recommendation, confidence } = summaryData;
  const finalScoreColor = getScoreColor(token, summary.finalScore);
  const recommendationColor = getRecommendationColor(summary.recommendation);
  const confidenceColor = getConfidenceColor(summary.confidence);
  const kpiIconStyle = { fontSize: token.fontSizeLG + 4, marginRight: token.marginXS };
  const kpiTagStyle = {
    fontSize: token.fontSizeLG,
    padding: `${token.paddingSM + 2}px ${token.paddingLG + 4}px`,
    fontWeight: 600,
    marginBottom: 0,
    borderRadius: token.borderRadius
  };
  const kpiReasonStyle = {
    fontSize: token.fontSizeSM,
    margin: 0,
    lineHeight: 1.7,
    color: token.colorTextSecondary
  };
  const listItemStyle = (index: number, total: number) => ({
    paddingTop: token.paddingMD,
    paddingBottom: token.paddingMD,
    borderBottom: index < total - 1 ? `1px solid ${token.colorBorderSecondary}` : 'none'
  });
  const indexBadgeStyle = (backgroundColor: string) => ({
    backgroundColor,
    minWidth: 26,
    height: 26,
    lineHeight: '26px',
    borderRadius: '50%',
    fontSize: token.fontSizeSM,
    fontWeight: 600
  });
  const evidenceStyle = {
    fontSize: token.fontSizeSM,
    marginLeft: 34,
    lineHeight: 1.7,
    display: 'block',
    color: token.colorTextSecondary
  };

  return (
    <PageTransition animation="fadeInUp">
      <DetailPageHeader
        icon={<SolutionOutlined />}
        title={t('summary.title')}
        backTo={MODULE_BASE_ROUTE}
        backLabel={t('summary.backToApplications')}
        meta={
          application ? (
            <>
              <Text type="secondary">
                <UserOutlined style={{ marginInlineEnd: token.marginXXS }} />
                {application.name}
              </Text>
              <Text type="secondary" ellipsis={{ tooltip: application.email }} style={{ minWidth: 0 }}>
                <MailOutlined style={{ marginInlineEnd: token.marginXXS }} />
                {application.email}
              </Text>
              <Text type="secondary">
                <SolutionOutlined style={{ marginInlineEnd: token.marginXXS }} />
                {application.position}
              </Text>
            </>
          ) : undefined
        }
      />

      {/* Final Score & Recommendation Cards */}
      <Row gutter={[token.marginMD, token.marginMD]} style={{ marginBottom: token.marginLG }}>
        <Col xs={24} md={8} style={{ display: 'flex' }}>
          <SummaryStatCard
            icon={<FireOutlined />}
            label={t('summary.finalScore')}
            footer={
              <Progress
                percent={formatScore(summary.finalScore)}
                strokeColor={finalScoreColor}
                showInfo={false}
                strokeWidth={10}
                strokeLinecap="round"
                railColor={token.colorFillTertiary}
              />
            }
          >
            <Statistic
              value={formatScore(summary.finalScore)}
              suffix="%"
              prefix={<TrophyOutlined style={{ color: finalScoreColor, fontSize: token.fontSizeHeading2 }} />}
              styles={{
                content: {
                  color: finalScoreColor,
                  fontSize: token.fontSizeHeading1,
                  fontWeight: 700,
                  lineHeight: 1.2
                }
              }}
            />
          </SummaryStatCard>
        </Col>
        <Col xs={24} md={8} style={{ display: 'flex' }}>
          <SummaryStatCard icon={<ThunderboltOutlined />} label={t('summary.recommendation')}>
            <Space align="center" style={{ width: '100%' }}>
              {summary.recommendation.toLowerCase().includes('no') ? (
                <DislikeOutlined style={{ color: recommendationColor, ...kpiIconStyle }} />
              ) : summary.recommendation.toLowerCase().includes('yes') ? (
                <LikeOutlined style={{ color: recommendationColor, ...kpiIconStyle }} />
              ) : (
                <WarningOutlined style={{ color: recommendationColor, ...kpiIconStyle }} />
              )}
              <Tag color={recommendationColor} style={kpiTagStyle}>
                {summary.recommendation.replace('_', ' ').toUpperCase()}
              </Tag>
            </Space>
            {recommendation.reason && (
              <Paragraph type="secondary" style={kpiReasonStyle}>
                {recommendation.reason}
              </Paragraph>
            )}
          </SummaryStatCard>
        </Col>
        <Col xs={24} md={8} style={{ display: 'flex' }}>
          <SummaryStatCard icon={<SafetyOutlined />} label={t('summary.confidence')}>
            <Space align="center" style={{ width: '100%' }}>
              {summary.confidence.toLowerCase() === 'high' ? (
                <RocketOutlined style={{ color: confidenceColor, ...kpiIconStyle }} />
              ) : summary.confidence.toLowerCase() === 'medium' ? (
                <InfoCircleOutlined style={{ color: confidenceColor, ...kpiIconStyle }} />
              ) : (
                <WarningOutlined style={{ color: confidenceColor, ...kpiIconStyle }} />
              )}
              <Tag color={confidenceColor} style={kpiTagStyle}>
                {summary.confidence.toUpperCase()}
              </Tag>
            </Space>
            {confidence.reason && (
              <Paragraph type="secondary" style={kpiReasonStyle}>
                {confidence.reason}
              </Paragraph>
            )}
          </SummaryStatCard>
        </Col>
      </Row>

      {/* Overall Fit Summary */}
      <SummarySectionCard
        icon={<InfoCircleOutlined />}
        title={t('summary.overallFit')}
        style={{ marginBottom: token.marginLG }}
      >
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Space align="start" style={{ width: '100%' }}>
              {summaryData.summary.overall_fit.toLowerCase().includes('no') || summaryData.summary.overall_fit.toLowerCase().includes('not a match') ? (
                <CloseCircleOutlined style={{ color: token.colorError, fontSize: token.fontSizeLG + 6, marginTop: 2, flexShrink: 0 }} />
              ) : summaryData.summary.overall_fit.toLowerCase().includes('yes') || summaryData.summary.overall_fit.toLowerCase().includes('match') ? (
                <CheckCircleOutlined style={{ color: token.colorSuccess, fontSize: token.fontSizeLG + 6, marginTop: 2, flexShrink: 0 }} />
              ) : (
                <InfoCircleOutlined style={{ color: token.colorWarning, fontSize: token.fontSizeLG + 6, marginTop: 2, flexShrink: 0 }} />
              )}
              <Text strong style={{ fontSize: token.fontSizeLG + 2, color: token.colorTextHeading, lineHeight: 1.7, flex: 1 }}>
                {summaryData.summary.overall_fit}
              </Text>
            </Space>
          </div>

          {summaryData.summary.top_matches && summaryData.summary.top_matches.length > 0 && (
            <div>
              <Title level={5} style={{ marginBottom: token.marginMD, marginTop: token.marginMD }}>
                <CheckCircleOutlined style={{ color: token.colorSuccess, marginRight: token.marginXS }} />
                {t('summary.topMatches')}
              </Title>
              {summaryData.summary.top_matches.map((item, index) => (
                <div key={index} style={{ paddingTop: token.paddingSM, paddingBottom: token.paddingSM }}>
                  <Space align="start">
                    <CheckCircleOutlined style={{ color: token.colorSuccess, marginTop: 4, fontSize: token.fontSizeLG }} />
                    <Text style={{ fontSize: token.fontSize, lineHeight: 1.7 }}>{item}</Text>
                  </Space>
                </div>
              ))}
            </div>
          )}

          {summaryData.summary.biggest_gaps && summaryData.summary.biggest_gaps.length > 0 && (
            <div>
              <Title level={5} style={{ marginBottom: token.marginMD, marginTop: token.marginMD }}>
                <WarningOutlined style={{ color: token.colorWarning, marginRight: token.marginXS }} />
                {t('summary.biggestGaps')}
              </Title>
              {summaryData.summary.biggest_gaps.map((item, index) => (
                <div key={index} style={{ paddingTop: token.paddingSM, paddingBottom: token.paddingSM }}>
                  <Space align="start">
                    <CloseCircleOutlined style={{ color: token.colorError, marginTop: 4, fontSize: token.fontSizeLG }} />
                    <Text style={{ fontSize: token.fontSize, lineHeight: 1.7 }}>{item}</Text>
                  </Space>
                </div>
              ))}
            </div>
          )}
        </Space>
      </SummarySectionCard>

      <Row gutter={[20, 20]} style={{ marginBottom: token.marginLG }}>
        {/* Pros */}
        <Col xs={24} lg={12}>
          <SummarySectionCard
            icon={<StarOutlined style={{ color: token.colorSuccess }} />}
            title={t('summary.strengths', { count: pros.length })}
            style={{ height: '100%' }}
          >
            {pros.map((item, index) => (
              <div key={index} style={listItemStyle(index, pros.length)}>
                <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                  <Space align="start">
                    <Badge count={index + 1} style={indexBadgeStyle(token.colorSuccess)} />
                    <Text strong style={{ fontSize: token.fontSize, lineHeight: 1.6 }}>{item.point}</Text>
                  </Space>
                  <Text type="secondary" style={evidenceStyle}>
                    <FileTextOutlined style={{ marginRight: token.marginXS, color: token.colorTextTertiary }} />
                    {decodeHtmlEntities(item.evidence)}
                  </Text>
                </Space>
              </div>
            ))}
          </SummarySectionCard>
        </Col>

        {/* Cons */}
        <Col xs={24} lg={12}>
          <SummarySectionCard
            icon={<WarningOutlined style={{ color: token.colorWarning }} />}
            title={t('summary.areasForImprovement', { count: cons.length })}
            style={{ height: '100%' }}
          >
            {cons.map((item, index) => (
              <div key={index} style={listItemStyle(index, cons.length)}>
                <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                  <Space align="start">
                    <Badge
                      count={index + 1}
                      style={indexBadgeStyle(item.severity === 'high' ? token.colorError : token.colorWarning)}
                    />
                    <Text strong style={{ fontSize: token.fontSize, lineHeight: 1.6 }}>{item.point}</Text>
                    {item.severity && (
                      <Tag color={item.severity === 'high' ? 'red' : 'orange'} style={{ borderRadius: token.borderRadius }}>
                        {item.severity}
                      </Tag>
                    )}
                  </Space>
                  <Text type="secondary" style={evidenceStyle}>
                    <FileTextOutlined style={{ marginRight: token.marginXS, color: token.colorTextTertiary }} />
                    {decodeHtmlEntities(item.evidence)}
                  </Text>
                </Space>
              </div>
            ))}
          </SummarySectionCard>
        </Col>
      </Row>

      {/* Detailed Score Breakdown */}
      <SummarySectionCard
        icon={<BulbOutlined />}
        title={t('summary.scoreBreakdown')}
        style={{ marginBottom: token.marginLG }}
      >
        <Row gutter={[16, 16]}>
          {Object.entries(scores).map(([key, value]) => {
            const scoreValue = typeof value === 'number' ? value : 0;
            const scoreColor = getScoreColor(token, scoreValue);
            const label = key
              .split('_')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
            return (
              <Col xs={24} sm={12} md={8} lg={6} key={key}>
                <Card
                  hoverable
                  size="small"
                  style={{ height: '100%' }}
                  styles={{
                    body: { padding: token.paddingMD + 4 }
                  }}
                >
                  <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary" style={{ fontSize: token.fontSizeSM, fontWeight: 500 }}>
                      {label}
                    </Text>
                    <Text strong style={{ fontSize: token.fontSizeLG + 4, color: scoreColor, fontWeight: 700 }}>
                      {formatScore(scoreValue)}%
                    </Text>
                    <Progress
                      percent={formatScore(scoreValue)}
                      strokeColor={scoreColor}
                      showInfo={false}
                      strokeWidth={8}
                      strokeLinecap="round"
                      railColor={token.colorFillTertiary}
                    />
                  </Space>
                </Card>
              </Col>
            );
          })}
        </Row>
      </SummarySectionCard>

      {/* Metadata */}
      <SummarySectionCard icon={<InfoCircleOutlined />} title={t('summary.applicationDetails')}>
        <Space orientation="vertical" size="small" style={{ width: '100%' }}>
          <InfoRow icon={<IdcardOutlined />} label={t('summary.applicationId')} value={summary.applicationId} />
          <InfoRow
            icon={<CalendarOutlined />}
            label={t('summary.created')}
            value={formatDateTime(summary.createdAt, SUMMARY_DATE_TIME_FORMAT)}
          />
          <InfoRow
            icon={<ClockCircleOutlined />}
            label={t('summary.lastUpdated')}
            value={formatDateTime(summary.updatedAt, SUMMARY_DATE_TIME_FORMAT)}
          />
        </Space>
      </SummarySectionCard>
    </PageTransition>
  );
}
